import React, { useEffect, useState } from 'react';
import './BillingDataPage.scss';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import Loader from '../../../shared/components/common/loader/Loader';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { useNavigate } from 'react-router-dom';
import { countryOptions, isAdminOrOwner } from '../../../shared/utils/utils';
import { getBillingData, saveBillingData } from '../../../shared/services/pricing/PricingService';
import {
  AddressData,
  BillingDataResponse,
  BusinessData,
} from '../../../shared/models/user/data/BillingDataResponse';
import { MY_ACCOUNT } from '../../../shared/routes/path';
import { Dropdown } from 'primereact/dropdown';
import DropdownItem from '../../../shared/components/dropdown-item/DropdownItem';

const BillingDataPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const navigate = useNavigate();
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<{ name: string; code: string } | null>(null);
  const [businessData, setBusinessData] = useState<BusinessData | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  if (accountResponse && !isAdminOrOwner(accountResponse.role)) {
    navigate(MY_ACCOUNT);
  }

  const onSubmit = async (formData: any) => {
    const data = {
      addressData: {
        countryCode: selectedCountry?.code,
        firstLine: formData.firstLine,
        secondLine: formData.secondLine,
        postalCode: formData.postalCode,
        city: formData.city,
      },
      businessData: {
        name: formData.companyName,
        companyNumber: formData.contactNumber,
        taxId: formData.taxId,
        contactEmail: formData.contactEmail,
        contactName: formData.contactName,
      },
    } as BillingDataResponse;
    saveBillingData(data).then(() => billingDataChangedDialog());
  };

  useEffect(() => {
    refreshData();
  }, []);

  const refreshData = () => {
    getBillingData().then((data) => {
      const country = countryOptions.find((country) => country.code === data.addressData?.countryCode);
      setSelectedCountry(country || null);
      setAddressData(data.addressData || {});
      setBusinessData(data.businessData || {});
    });
  };

  const billingDataChangedDialog = () => {
    confirmDialog({
      message: 'Your data has been successfully changed.',
      header: 'Data Changed',
      defaultFocus: 'accept',
      acceptLabel: 'OK',
      accept: () => refreshData(),
    });
  };

  if (!addressData && !businessData) {
    return (
      <div className="billing-data-page">
        <Loader />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="billing-data-page">
      <ConfirmDialog className="dialog-box dialog-box--accept" />

      <div className="billing-data-page__row">
        <div className="form form--inline">
          <div className="form__row form__row--inline">
            <div className="form__label">{t('companyName')}:</div>
            <InputText {...register('companyName')} defaultValue={businessData?.name} type="text" />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('phoneNumber')}:</div>
            <InputText
              {...register('contactNumber')}
              defaultValue={businessData?.companyNumber}
              type="text"
            />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('taxId')}:</div>
            <InputText {...register('taxId')} defaultValue={businessData?.taxId || ''} type="text" />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('contactName')}:</div>
            <InputText
              {...register('contactName')}
              defaultValue={businessData?.contactName}
              type="text"
            />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('contactEmail')}:</div>
            <InputText
              {...register('contactEmail', {
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Please enter a valid email address',
                },
              })}
              defaultValue={businessData?.contactEmail}
              type="text"
            />
            {errors.contactEmail && <span>{errors.contactEmail.message as string}</span>}
          </div>

          <div className="form__row form__row--inline">
            <div className="form__label">{t('firstLineAddress')}:</div>
            <InputText type="text" {...register('firstLine')} defaultValue={addressData?.firstLine} />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('secondLineAddress')}:</div>
            <InputText type="text" {...register('secondLine')} defaultValue={addressData?.secondLine} />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('zipCode')}:</div>
            <InputText
              {...register('postalCode')}
              defaultValue={addressData?.postalCode}
              type="text"
              className={errors.postalCode ? 'form__invalid' : ''}
            />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('city')}:</div>
            <InputText {...register('city')} type="text" defaultValue={addressData?.city} />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('country')}:</div>
            <Dropdown
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.value)}
              options={countryOptions}
              className="dropdown"
              optionLabel="name"
              itemTemplate={(option) => <DropdownItem name={option.name} />}
            />
          </div>
          <Button
            className="btn btn--sm settings-page__save"
            label={t('save')}
            icon={<span className="ico--tick ico--xs" />}
          />
        </div>
        <div className="billing-data-page__help">
          <div className="help-box">
            <label className="help-box__title">{t('changeBillingAddress')}</label>
            <div>
              Update the address where your billing information is sent. Ensure your details are accurate
              to avoid interruptions in service or issues with payment processing. This address will be
              used for invoicing and any related correspondence. Please provide the correct street, city,
              state, and postal code to keep your account up-to-date.
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default BillingDataPage;
